import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, Checkbox, ColorPicker, Flex, Select, Stack, Text, TextInput } from "@mantine/core";
import { AppModal } from "../../shared/AppModal";
import { useForm, yupResolver } from "@mantine/form";
import { useEffect } from "react";
import { useCreatePriorityStatusMutation, useGetSimpleEmailTemplatesQuery, useUpdatePriorityStatusMutation } from "../../graphql/graphql";
import { confettiRandom } from "../../utils/Confetti.utils";
import { getFormSchemaData } from "../../utils/Form.utils";
import { createSingleLinePreview } from "../../utils/String.utils";
import { showErrorNotification, showSuccessNotification } from "../../shared/Notifications";
import { PriorityStatusInitialValues, PriorityStatusSchema } from "./PriorityStatus.validation";
export const PriorityStatusModal = ({ priorityStatus, sortOrder, existingColorHexes, opened, close }) => {
    const [{ fetching: createFetching }, createPriorityStatus] = useCreatePriorityStatusMutation();
    const [{ fetching: updateFetching }, updatePriorityStatus] = useUpdatePriorityStatusMutation();
    const [{ data: simpleEmailTemplateData }] = useGetSimpleEmailTemplatesQuery({
        pause: !opened,
        variables: {
            offset: 0
        }
    });
    const form = useForm({
        mode: "controlled",
        initialValues: PriorityStatusInitialValues,
        validate: yupResolver(PriorityStatusSchema)
    });
    useEffect(() => {
        if (!!priorityStatus) {
            form.setValues(getFormSchemaData(PriorityStatusSchema, priorityStatus));
            form.setFieldValue("throwConfetti", !!priorityStatus.throwConfetti);
            form.setFieldValue("simpleEmailTemplateId", priorityStatus.simpleEmailTemplateId ?? null);
        }
        else {
            form.setValues(PriorityStatusInitialValues);
        }
        form.resetDirty();
    }, [opened, priorityStatus]);
    const submitPriorityStatus = async (event) => {
        event.preventDefault();
        const valid = form.validate();
        if (valid.hasErrors)
            return;
        const formValues = form.getValues();
        try {
            const response = priorityStatus
                ? updatePriorityStatus({
                    priorityStatusId: priorityStatus.priorityStatusId,
                    name: formValues.name,
                    colorHex: formValues.colorHex,
                    sortOrder: formValues.sortOrder,
                    throwConfetti: formValues.throwConfetti,
                    simpleEmailTemplateId: formValues.simpleEmailTemplateId || null
                })
                : createPriorityStatus({
                    name: formValues.name,
                    colorHex: formValues.colorHex,
                    sortOrder: formValues.sortOrder ?? sortOrder,
                    throwConfetti: formValues.throwConfetti,
                    simpleEmailTemplateId: formValues.simpleEmailTemplateId || null
                });
            response
                .then(() => {
                showSuccessNotification(`Referral stage was ${priorityStatus ? "updated" : "created"} successfully!`);
            })
                .catch((error) => {
                console.error("An error occurred.", error);
                showErrorNotification();
            })
                .finally(() => {
                handleClose();
            });
        }
        catch (error) {
            console.error("An error occurred.", error);
            showErrorNotification();
        }
    };
    const handleClose = () => {
        close();
        form.reset();
    };
    // If we're not expressly saying this status can't be edited, then it can be edited.
    const isMutable = priorityStatus?.isMutable ?? true;
    const simpleEmailTemplates = simpleEmailTemplateData?.simpleEmailTemplates?.simpleEmailTemplates ??
        [];
    const simpleEmailTemplateOptions = simpleEmailTemplates.map((template) => ({
        value: template.simpleEmailTemplateId,
        label: template.subject,
        bodyPreview: createSingleLinePreview(template.body)
    }));
    return (_jsxs(AppModal, { title: `${priorityStatus ? "Update" : "Add"} Referral Stage`, opened: opened, onClose: handleClose, children: [_jsxs(Stack, { gap: "sm", align: "center", style: { marginBottom: "5rem" }, children: [_jsx(TextInput, { "data-autofocus": isMutable, readOnly: !isMutable, required: true, label: "Name", placeholder: "Name", style: { width: "80%" }, styles: {
                            input: {
                                backgroundColor: form.values.colorHex,
                                cursor: isMutable ? "default" : "not-allowed"
                            }
                        }, ...form.getInputProps("name") }), _jsx(ColorPicker, { autoFocus: !isMutable, format: "hex", ...form.getInputProps("colorHex"), swatches: existingColorHexes || [] }), form.errors.colorHex && (_jsx(Text, { variant: "body", size: "sm", c: "var(--mantine-color-red-5)", children: "A color is required" })), _jsx(Checkbox, { label: "Throw Confetti?", checked: form.values.throwConfetti, onChange: (event) => {
                            if (event.currentTarget.checked) {
                                confettiRandom();
                            }
                            form.setFieldValue("throwConfetti", event.currentTarget.checked);
                        } }), _jsx(Select, { label: "Email Template", placeholder: "No email template", clearable: true, searchable: true, style: { width: "80%" }, data: simpleEmailTemplateOptions, value: form.values.simpleEmailTemplateId ?? null, styles: {
                            option: {
                                paddingTop: 8,
                                paddingBottom: 8
                            }
                        }, renderOption: ({ option }) => {
                            const selectedOption = simpleEmailTemplateOptions.find((templateOption) => templateOption.value === option.value);
                            return (_jsxs(Text, { style: {
                                    minWidth: 0,
                                    flex: 1,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap"
                                }, size: "sm", children: [_jsx("span", { style: { fontWeight: 700 }, children: option.label }), _jsx("span", { style: {
                                            color: "var(--mantine-color-dimmed)"
                                        }, children: selectedOption?.bodyPreview
                                            ? ` - ${selectedOption.bodyPreview}`
                                            : "" })] }));
                        }, onChange: (value) => form.setFieldValue("simpleEmailTemplateId", value) })] }), _jsx(Flex, { justify: "flex-end", children: _jsx(Button, { variant: "filled", loading: createFetching || updateFetching, onClick: submitPriorityStatus, children: priorityStatus ? "Update" : "Create" }) })] }));
};
